///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { GtthNotificationsService } from '../../project-view-panel/logical-topology/engine/notifications-service';
import { catchError } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import {
    ExceedingMaxHopsDataDto,
    ExceedingMaxLinksDataDto,
    GtthConstraintComplianceDTO,
    LinkDataInfoReportDto,
    TopologyValidityDTO
} from '../../generated/shared-types.model';
import { GtthProjectStateService } from '../../project-view-panel/logical-topology/engine/project-state.service';
import { ExtendedSectorData } from '../../project-view-panel/project-windows/reports/p2mp-info/extended-sector-data.model';

export enum ReportType {
    CONSTRAINT_COMPLIANCE = 'constraint-compliance',
    CONNECTED_SITES = 'connected-sites',
    P2P = 'P2P',
    P2MP = 'P2MP',
    MAX_LINKS = 'max-links',
    MAX_HOPS = 'max-hops',
    INTERFERENCE = 'interference'
}

@Injectable({ providedIn: 'root' })
export class GtthReportsService {
    private readonly reportsUrl = 'api/reports';

    private topologyValidity: TopologyValidityDTO;

    constructor(
        private readonly http: HttpClient,
        private readonly notificationService: GtthNotificationsService,
        private readonly projectStateService: GtthProjectStateService
    ) {}

    generateConstraintComplianceReport(): Observable<GtthConstraintComplianceDTO> {
        if (this.projectStateService.projectIsOpened()) {
            return this.http
                .get<GtthConstraintComplianceDTO>(this.getUrl(ReportType.CONSTRAINT_COMPLIANCE))
                .pipe(catchError(this.handleReportError<GtthConstraintComplianceDTO>({} as GtthConstraintComplianceDTO)));
        } else {
            return of({} as GtthConstraintComplianceDTO);
        }
    }

    getExceedingMaxLinksNodes(): Observable<ExceedingMaxLinksDataDto[]> {
        if (this.projectStateService.projectIsOpened()) {
            return this.http
                .get<ExceedingMaxLinksDataDto[]>(this.getUrl(ReportType.MAX_LINKS))
                .pipe(catchError(this.handleReportError<ExceedingMaxLinksDataDto[]>([] as ExceedingMaxLinksDataDto[])));
        } else {
            return of([] as ExceedingMaxLinksDataDto[]);
        }
    }

    getExceedingMaxHopsNodes(): Observable<ExceedingMaxHopsDataDto[]> {
        if (this.projectStateService.projectIsOpened()) {
            return this.http
                .get<ExceedingMaxHopsDataDto[]>(this.getUrl(ReportType.MAX_HOPS))
                .pipe(catchError(this.handleReportError<ExceedingMaxHopsDataDto[]>([] as ExceedingMaxHopsDataDto[])));
        } else {
            return of([] as ExceedingMaxHopsDataDto[]);
        }
    }

    getLinksEndsWithInterference(): Observable<any> {
        if (this.projectStateService.projectIsOpened()) {
            return this.http.get<any>(this.getUrl(ReportType.INTERFERENCE)).pipe(catchError(this.handleReportError<any>({})));
        } else {
            return of({});
        }
    }

    validateTopologyAfterDesign(): Observable<TopologyValidityDTO> {
        if (this.projectStateService.projectIsOpened()) {
            return this.http
                .get<TopologyValidityDTO>(`/api/topology/${this.projectStateService.getProjectId()}/validate-after-design`)
                .pipe(catchError(this.handleReportError<TopologyValidityDTO>({} as TopologyValidityDTO)));
        } else {
            return of({} as TopologyValidityDTO);
        }
    }

    validateTopologyBeforeDesign(): Observable<TopologyValidityDTO> {
        if (this.projectStateService.projectIsOpened()) {
            return this.http
                .get<TopologyValidityDTO>(`/api/topology/${this.projectStateService.getProjectId()}/validate-before-design`)
                .pipe(catchError(this.handleReportError<TopologyValidityDTO>({} as TopologyValidityDTO)));
        } else {
            return of({} as TopologyValidityDTO);
        }
    }

    // prevent generating same report twice before design with unconnected nodes
    setTopologyValidityToCache(topologyValidity: TopologyValidityDTO) {
        this.topologyValidity = topologyValidity;
    }

    // used in connected-sites report
    getTopologyValidityDtoFromCache() {
        return this.topologyValidity;
    }

    generateP2PInfoReport(): Observable<LinkDataInfoReportDto[]> {
        if (this.projectStateService.projectIsOpened()) {
            return this.http
                .get<LinkDataInfoReportDto[]>(this.getUrl(ReportType.P2P))
                .pipe(catchError(this.handleReportError<LinkDataInfoReportDto[]>([])));
        } else {
            return of([]);
        }
    }

    generateP2MPInfoReport(): Observable<ExtendedSectorData[]> {
        if (this.projectStateService.projectIsOpened()) {
            return this.http
                .get<ExtendedSectorData[]>(this.getUrl(ReportType.P2MP))
                .pipe(catchError(this.handleReportError<ExtendedSectorData[]>([])));
        } else {
            return of([]);
        }
    }

    private handleReportError<T>(result?: T) {
        return (error: any): Observable<T> => {
            this.notificationService.showHttpError(error);
            return of(result);
        };
    }

    private getUrl(type: string) {
        let url = `${this.reportsUrl}/${this.projectStateService.getProjectId()}`;
        switch (type) {
            case ReportType.CONSTRAINT_COMPLIANCE:
            case ReportType.CONNECTED_SITES:
            case ReportType.P2P:
            case ReportType.P2MP:
                url = `${url}/${type}`;
                break;
            case ReportType.MAX_LINKS:
            case ReportType.MAX_HOPS:
            case ReportType.INTERFERENCE:
                url = `${url}/constraint-compliance/${type}`;
                break;
            default:
                console.error('No such report type!');
        }
        return url;
    }
}
